import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import useAxiosConfig from "../config/AxiosConfig";

const ResetGroundPasswordModal = ({ open, ground, onClose, onSuccess }) => {
  const axiosInstance = useAxiosConfig();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phoneNoInput, setPhoneNoInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const managerId = useMemo(
    () => ground?.owner?._id || ground?.owner?.id || "",
    [ground]
  );
  const fullName = ground?.owner?.fullName || ground?.managedBy || "";
  const phoneNo = ground?.owner?.phone_no || "";

  useEffect(() => {
    if (open) {
      setPhoneNoInput(phoneNo);
    } else {
      setPassword("");
      setConfirmPassword("");
      setPhoneNoInput("");
      setIsSubmitting(false);
      setErrorMessage("");
      setSuccessMessage("");
    }
  }, [open, phoneNo]);

  const handleResetPassword = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!managerId) {
      setErrorMessage("Manager id not found for this ground.");
      return;
    }

    const isPhoneChanged = phoneNoInput !== phoneNo;
    const isAnyPasswordFieldFilled = !!password || !!confirmPassword;

    if (!isPhoneChanged && !isAnyPasswordFieldFilled) {
      setErrorMessage("Please change phone number or password to continue.");
      return;
    }

    if (isAnyPasswordFieldFilled) {
      if (!password || !confirmPassword) {
        setErrorMessage("Please fill in both password fields.");
        return;
      }

      if (password !== confirmPassword) {
        setErrorMessage("Password and confirm password do not match.");
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const payload = {
        fullName,
        phone_no: phoneNoInput,
      };

      if (isAnyPasswordFieldFilled) {
        payload.password = password;
        payload.confirmPassword = confirmPassword;
      }

      await axiosInstance.put(
        `/api/admin/managers/update-manager/${managerId}`,
        payload
      );
      if (onSuccess) {
        onSuccess({
          groundId: ground?._id,
          phone_no: phoneNoInput,
        });
      }
      setSuccessMessage("Manager updated successfully.");
      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to reset password.";
      setErrorMessage(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Manager</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {ground ? `Ground: ${ground.name}` : "Ground password reset modal"}
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          label="Phone Number"
          value={phoneNoInput}
          onChange={(e) => setPhoneNoInput(e.target.value)}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        {!!errorMessage && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessage}
          </Alert>
        )}
        {!!successMessage && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {successMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleResetPassword}
          disabled={isSubmitting}
          sx={{
            backgroundColor: "#0C7240",
            color: "white",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#0C7240",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              backgroundColor: "#B0B0B0",
              color: "white",
            },
          }}
        >
          {isSubmitting ? "Updating..." : "Update Manager"}
        </Button>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            backgroundColor: "#0C7240",
            color: "white",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#0C7240",
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetGroundPasswordModal;
